import {Button, Collapse, Result, Space} from 'antd';
import {
  ERROR_ACTIONS,
  createErrorActionHandlers,
  getActionLabel,
  getErrorDisplayInfo,
  handleApiError,
} from 'middleware/errorHandler';
import React, {Component, forwardRef, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectUserContext} from 'store/authSlice';
import errorLogger, {ComponentError} from 'utils/errors';

/**
 * 1. Functional Fallback Component
 * Handles UI logic, Redux hooks, and Action Handlers
 */
const ErrorFallback = ({
  error,
  errorInfo,
  resetError,
  componentName,
  showDetails,
  showErrorMessage,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUserContext);
  const errorDisplayInfo = getErrorDisplayInfo(error);

  // Use our centralized handler factory - now injected with dispatch from hook
  const actionHandlers = createErrorActionHandlers(
    {
      onClose: resetError,
      customHandlers: {
        [ERROR_ACTIONS.RETRY]: resetError,
        [ERROR_ACTIONS.HOME]: () => {
          window.location.href = '/';
          resetError();
        },
      },
    },
    dispatch
  );

  const subtitle =
    (showErrorMessage && error?.message) ||
    errorDisplayInfo.message ||
    'An unexpected error occurred in the application interface.';

  return (
    <Result
      status="error"
      title={errorDisplayInfo.title || 'Something went wrong'}
      subTitle={subtitle}
      extra={
        <Space>
          {errorDisplayInfo.actions?.map((action) => (
            <Button
              key={action}
              onClick={actionHandlers[action]}
              type={action === ERROR_ACTIONS.RETRY ? 'primary' : 'default'}
            >
              {getActionLabel(action)}
            </Button>
          ))}
        </Space>
      }
    >
      <div style={{textAlign: 'left'}}>
        {showDetails ? (
          <div>
            <strong>Error Details</strong>
            <div>
              <strong>Component Stack:</strong>
              <pre
                style={{
                  fontSize: '11px',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  overflow: 'auto',
                }}
              >
                {`Component: ${componentName}\n`}
                {`User: ${user?.email || 'Anonymous'}\n`}
                {`Stack: ${errorInfo?.componentStack}`}
              </pre>
            </div>
          </div>
        ) : (
          <Collapse
            ghost
            items={[
              {
                key: '1',
                label: 'Technical Details',
                children: (
                  <pre
                    style={{
                      fontSize: '11px',
                      padding: '12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      overflow: 'auto',
                    }}
                  >
                    {`Component: ${componentName}\n`}
                    {`User: ${user?.email || 'Anonymous'}\n`}
                    {`Stack: ${errorInfo?.componentStack}`}
                  </pre>
                ),
              },
            ]}
          />
        )}
      </div>
    </Result>
  );
};

/**
 * 2. Main Error Boundary Class
 * Strictly for catching errors (since hooks can't do this yet)
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false, error: null, errorInfo: null};
  }

  static getDerivedStateFromError(error) {
    return {hasError: true, error};
  }

  componentDidCatch(error, errorInfo) {
    // Log to Datadog/Console via our store-agnostic utility
    errorLogger.logComponentError(
      error,
      this.props.componentName || 'UnknownComponent',
      {errorInfo, props: this.props.errorContext}
    );
    this.setState({errorInfo});
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({hasError: false, error: null, errorInfo: null});
  };

  render() {
    if (this.state.hasError) {
      const {fallback, componentName, showDetails, showErrorMessage} =
        this.props;

      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(
            this.state.error,
            this.state.errorInfo,
            this.resetError
          );
        }
        return fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
          componentName={componentName}
          showDetails={showDetails}
          showErrorMessage={showErrorMessage}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * 3. Modern HOC and Hooks
 */
export function withErrorBoundary(ComponentToWrap, errorBoundaryProps = {}) {
  const WrappedComponent = forwardRef((props, ref) => {
    const name =
      ComponentToWrap.displayName || ComponentToWrap.name || 'Component';
    return (
      <ErrorBoundary
        componentName={name}
        errorContext={props}
        {...errorBoundaryProps}
      >
        <ComponentToWrap {...props} ref={ref} />
      </ErrorBoundary>
    );
  });

  WrappedComponent.displayName = `withErrorBoundary(${ComponentToWrap.displayName || ComponentToWrap.name})`;
  return WrappedComponent;
}

/**
 * Hook for handling errors in functional components manually.
 * This can trigger the global API error UI (AntD message)
 * or re-throw to trigger the nearest ErrorBoundary.
 */
export function useErrorHandler() {
  const dispatch = useDispatch();
  const userContext = useSelector(selectUserContext);

  return useCallback(
    (error, options = {}) => {
      const {
        shouldThrow = true,
        showGlobalMessage = false,
        errorContext = {},
      } = options;

      const componentError = new ComponentError(
        error.message || 'Manual Error',
        {
          originalError: error,
          context: {...errorContext, userContext},
        }
      );

      // 1. Log the error
      errorLogger.logComponentError(
        componentError,
        'useErrorHandler',
        errorContext
      );

      // 2. If requested, trigger the global AntD message UI (like the middleware does)
      if (showGlobalMessage) {
        // We pass the dispatch from the hook into the handler
        handleApiError(componentError, {key: options.key}, dispatch);
      }

      // 3. Re-throw to hit the ErrorBoundary if shouldThrow is true
      if (shouldThrow) {
        throw componentError;
      }
    },
    [dispatch, userContext]
  );
}

/**
 * Hook for safely executing async operations, catching errors without
 * re-throwing them to the nearest ErrorBoundary.
 */
export function useSafeAsyncOperation() {
  return useCallback(async (asyncFn) => {
    try {
      return await asyncFn();
    } catch (error) {
      console.error('useSafeAsyncOperation caught error:', error);
    }
  }, []);
}

export default ErrorBoundary;
